/**
 * Normalises an axios error (or generic Error) into a **plain string** safe to
 * render inside a toast / paragraph.
 *
 * FastAPI can raise `HTTPException(detail=...)` with either a string OR a
 * dict/list. When the client naively renders the raw `detail` object, React
 * throws `Objects are not valid as a React child`. That crash is what caused
 * the P0 checkout black-screen bug — with no ErrorBoundary in place it
 * unmounted the entire app.
 *
 * Precedence:
 *   1. axios error.response.data.detail (string)  → returned as-is
 *   2. axios error.response.data.detail.message   → the human-readable msg
 *   3. any string field on the response body      → returned as-is
 *   4. error.message                              → generic axios / JS error
 *   5. fallback                                   → provided caller default
 */
export function errorMessage(err, fallback = "Something went wrong") {
  if (!err) return fallback;

  // Axios error shape
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (detail && typeof detail === "object") {
    if (typeof detail.message === "string" && detail.message.trim()) {
      return detail.message;
    }
    if (typeof detail.error === "string" && detail.error.trim()) {
      return detail.error;
    }
  }

  // Some FastAPI validators return {data: {error: '...'}} or {message: '...'}
  const body = err?.response?.data;
  if (typeof body === "string" && body.trim()) return body;
  if (body && typeof body === "object") {
    if (typeof body.message === "string" && body.message.trim()) return body.message;
    if (typeof body.error === "string" && body.error.trim()) return body.error;
  }

  if (typeof err.message === "string" && err.message.trim()) return err.message;
  return fallback;
}

/**
 * Optional companion helper — extracts a short opaque support reference from
 * an error response body when the backend provides one (see
 * `_customer_facing_checkout_error` in `backend/routers/billing.py`).
 * Returns "" when none is present. Never throws.
 */
export function supportReference(err) {
  // The axios response interceptor may have moved the original object to
  // `detail_raw` after normalizing `detail` to a string. Check both.
  const detail = err?.response?.data?.detail;
  const raw = err?.response?.data?.detail_raw;
  for (const candidate of [detail, raw]) {
    if (candidate && typeof candidate === "object") {
      const ref = candidate.support_reference || candidate.support_ref;
      if (typeof ref === "string" && ref.trim()) return ref;
    }
  }
  return "";
}
